import axios from "axios";
import { API_DOMAIN } from "./interceptors/elliemaeApi";

const normalize = v =>
    String(v || "")
        .trim()
        .toLowerCase();

/**
 * Checks whether a user has access to a page based on persona config
 *
 * @param {string} pageName
 * @param {object} user - elli auth user object
 * @returns {Promise<boolean>}
 */
export const hasPageAccess = async (pageName, user) => {
    try {
        const userPersonas = Array.isArray(user?.personas)
            ? user.personas
                  .map(p =>
                      String(p?.entityName || "")
                          .trim()
                          .toLowerCase()
                  )
                  .filter(Boolean)
            : [];

        if (!userPersonas.length) return false;

        const url = `${API_DOMAIN}/api/persona/page/${encodeURIComponent(pageName)}`;

        const response = await axios.get(url, {
            headers: { "Content-Type": "application/json" }
        });

        if (response.status !== 200 || !response.data) {
            return false;
        }

        const allowedNames = (response.data.allowedPersonas || []).map(p => String(p).trim().toLowerCase());

        return userPersonas.some(p => allowedNames.includes(p));
    } catch (error) {
        console.error("Error checking page access:", error);
        return false;
    }
};

/**
 * Checks if user persona is read-only for a page
 *
 * @param {string} pageName
 * @param {object} user
 * @returns {Promise<boolean>}
 */

export const getPagePersonaAccess = async pageName => {
    try {
        const url = `${API_DOMAIN}/api/persona/page/${encodeURIComponent(pageName)}`;

        const response = await axios.get(url, {
            headers: { "Content-Type": "application/json" }
        });

        if (response.status !== 200 || !response.data) {
            return null;
        }

        return response.data;
    } catch (error) {
        console.error("Error fetching page persona access:", error);
        return null;
    }
};

export const evaluatePageAndFieldAccess = (pageAccess, user) => {
    const userPersonas = Array.isArray(user?.personas) ? user.personas.map(p => normalize(p?.entityName)).filter(Boolean) : [];

    const userId = normalize(user?.id || user?.userId || user?.loginId);

    const readOnlyNames = (pageAccess?.readOnlyPersonas || []).map(normalize);

    const isPageReadOnly = userPersonas.some(p => readOnlyNames.includes(p));

    const fieldPermission = pageAccess?.fieldPermission || null;
    const restrictedFieldIds = Array.isArray(fieldPermission?.fieldIds) ? fieldPermission.fieldIds.filter(Boolean) : [];

    const editablePersonas = Array.isArray(fieldPermission?.editablePersonas) ? fieldPermission.editablePersonas.map(normalize) : [];

    const allowedUserIds = Array.isArray(fieldPermission?.userIds) ? fieldPermission.userIds.map(normalize) : [];

    const hasEditablePersona = editablePersonas.length > 0 && userPersonas.some(p => editablePersonas.includes(p));

    const hasAllowedUserId = !!userId && allowedUserIds.length > 0 && allowedUserIds.includes(userId);

    const canEditRestrictedFields = hasEditablePersona || hasAllowedUserId;

    return {
        isPageReadOnly,
        restrictedFieldIds,
        canEditRestrictedFields,
        pageAccess
    };
};

export const isReadOnlyPersona = async (pageName, user) => {
    const pageAccess = await getPagePersonaAccess(pageName);
    if (!pageAccess) return false;

    const result = evaluatePageAndFieldAccess(pageAccess, user);
    return result.isPageReadOnly;
};

/**
 * Gets milestone persona permissions for the Loan Approval page.
 * Throws when the endpoint fails so the page can fail closed.
 *
 * @param {string} pageName
 * @returns {Promise<Object>}
 */
export const fetchMilestonePersonaPermission = async pageName => {
    if (!pageName || !pageName.trim()) {
        throw new Error("Page name is required.");
    }

    const url = `${API_DOMAIN}/api/persona/milestone/${encodeURIComponent(pageName.trim())}`;

    const response = await axios.get(url, {
        headers: {
            Accept: "application/json"
        }
    });

    return response.data;
};
